import { useCallback, useEffect, useMemo } from 'react';
import type * as Y from 'yjs';
import {
  birthdaySchema,
  CALENDAR_DOC,
  CALENDAR_EVENTS_KEY,
  deleteEventIn,
  detachOccurrenceIn,
  excludeOccurrenceIn,
  HOUSEHOLD_BIRTHDAYS_KEY,
  HOUSEHOLD_CALENDAR_PREFS_KEY,
  HOUSEHOLD_DOC,
  HOUSEHOLD_MEMBERS_KEY,
  householdMemberSchema,
  insertEvent,
  moveEventBetween,
  newEventPlacementFor,
  parseCalendarEvent,
  parseCalendarPref,
  personalCalendarDocName,
  restoreEventIn,
  updateEventIn,
  type Birthday,
  type CalendarEvent,
  type CalendarPref,
  type EventPatch,
  type HouseholdMember,
  type NewEventInput,
  type NewEventPlacement,
} from '@awwyeah/shared';
import { useYDoc, useYMap } from '../../sync';
import { mergeVisibleEvents } from './eventVisibility';
import { useUser } from '../auth';
import { memberIndex, toMemberViews, type MemberView } from './members';

/**
 * The calendar's Yjs surface. Reads come from the sync hooks' snapshots; **writes go
 * straight to the Y.Map** (docs/ARCHITECTURE.md § Sync layer) — no REST ever mutates
 * calendar state (ROADMAP §1.3). Every write stamps the audit fields — via the shared
 * mutation helpers (`@awwyeah/shared` calendar `mutations.ts`), the same code the server's
 * MCP connector writes with, so both produce identical records. This hook only decides
 * *which* map (family or personal doc) a write lands in.
 */

export type { EventPatch, NewEventInput };

/**
 * Where an event lives (WS-15). Two *different* mechanisms, and the difference is real:
 *
 *  - `'personal'` — the event is written to `calendar:user:<me>`, a doc the sync hub will
 *    not open for anybody else. **Hard.** This is what "Just me" means.
 *  - `'family'`   — the shared `calendar` doc. Optionally narrowed by the event's
 *    `audience` field, which is a *display* filter only. **Soft**: the event is still in a
 *    doc the whole household can open. Suitable for "don't clutter everyone's month view",
 *    not for a secret.
 */
export type EventPlacement = 'family' | 'personal';

export interface CalendarStore {
  /** Family + my personal events, merged and audience-filtered for me. */
  events: CalendarEvent[];
  byId: Map<string, CalendarEvent>;
  /** Which doc each visible event came from — the form needs it to pre-select "Just me". */
  placementOf: (eventId: string) => EventPlacement;
  createEvent: (input: NewEventInput, placement?: EventPlacement) => CalendarEvent;
  updateEvent: (id: string, patch: EventPatch) => CalendarEvent | null;
  /**
   * Moves an event between the shared and personal docs (flipping "Just me" on or off).
   * Write-then-delete across two docs, like the lists registry move, for the same reason:
   * "in both for a moment" is recoverable, "in neither" looks like a deletion.
   */
  setPlacement: (id: string, placement: EventPlacement) => CalendarEvent | null;
  deleteEvent: (id: string) => CalendarEvent | null;
  /** Puts a deleted event back exactly as it was (the undo toast). */
  restoreEvent: (event: CalendarEvent) => void;
  /**
   * "Just this one": excludes `occurrenceStart` from the series and writes a detached
   * event carrying the edit, in a single transaction so peers never see a gap.
   */
  detachOccurrence: (
    parentId: string,
    occurrenceStart: string,
    patch: EventPatch,
  ) => CalendarEvent | null;
  /** Removes a single occurrence of a repeating event. */
  excludeOccurrence: (parentId: string, occurrenceStart: string) => void;
}

function newId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `evt-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

export function useCalendarStore(): CalendarStore {
  const { user } = useUser();
  const doc = useYDoc(CALENDAR_DOC);
  // Hooks can't be conditional; with no signed-in member this opens `calendar` twice,
  // which the DocManager resolves to the same doc and the same socket. `hasPersonal`
  // stops those entries being counted a second time.
  const personalDocName = user ? personalCalendarDocName(user.id) : CALENDAR_DOC;
  const personalDoc = useYDoc(personalDocName);
  const hasPersonal = Boolean(user);

  const { map, entries } = useYMap<CalendarEvent>(doc, CALENDAR_EVENTS_KEY);
  const { map: personalMap, entries: personalEntries } = useYMap<CalendarEvent>(
    personalDoc,
    CALENDAR_EVENTS_KEY,
  );
  const authorId = user?.id ?? 'unknown';
  const meId = user?.id ?? null;

  const familyEvents = useMemo(
    () =>
      entries
        .map(([, value]) => parseCalendarEvent(value))
        .filter((event): event is CalendarEvent => event !== null),
    [entries],
  );

  const personalEventsList = useMemo(
    () =>
      hasPersonal
        ? personalEntries
            .map(([, value]) => parseCalendarEvent(value))
            .filter((event): event is CalendarEvent => event !== null)
        : [],
    [personalEntries, hasPersonal],
  );

  const personalIds = useMemo(
    () => new Set(personalEventsList.map((event) => event.id)),
    [personalEventsList],
  );

  // One merge for every consumer — views, search overlay, day strip — so none of them has
  // to remember to apply the subset filter (`eventVisibility.ts`).
  const events = useMemo(
    () => mergeVisibleEvents(familyEvents, personalEventsList, meId),
    [familyEvents, personalEventsList, meId],
  );

  const byId = useMemo(() => new Map(events.map((event) => [event.id, event])), [events]);

  const placementOf = useCallback(
    (eventId: string): EventPlacement => (personalIds.has(eventId) ? 'personal' : 'family'),
    [personalIds],
  );

  /** The Y.Map an event id currently lives in. */
  const mapFor = useCallback(
    (eventId: string): Y.Map<CalendarEvent> =>
      hasPersonal && personalIds.has(eventId) ? personalMap : map,
    [hasPersonal, personalIds, personalMap, map],
  );

  const createEvent = useCallback(
    (input: NewEventInput, placement: EventPlacement = 'family'): CalendarEvent => {
      const target = placement === 'personal' && hasPersonal ? personalMap : map;
      return insertEvent(target, input, { actorId: authorId, newId });
    },
    [authorId, map, personalMap, hasPersonal],
  );

  const setPlacement = useCallback(
    (id: string, placement: EventPlacement): CalendarEvent | null => {
      if (!hasPersonal) return null;
      const current = placementOf(id);
      if (current === placement) return parseCalendarEvent(mapFor(id).get(id));
      const from = current === 'personal' ? personalMap : map;
      const to = placement === 'personal' ? personalMap : map;
      // Write-then-delete; moving into the personal doc drops any subset audience.
      return moveEventBetween(from, to, id, placement === 'personal', { actorId: authorId });
    },
    [authorId, hasPersonal, map, mapFor, personalMap, placementOf],
  );

  const updateEvent = useCallback(
    (id: string, patch: EventPatch): CalendarEvent | null => {
      // Every mutation below resolves its own Y.Map first: an event may live in either
      // doc, and writing a personal event back into the shared one would un-hide it.
      return updateEventIn(mapFor(id), id, patch, { actorId: authorId });
    },
    [authorId, mapFor],
  );

  const deleteEvent = useCallback(
    (id: string): CalendarEvent | null => deleteEventIn(mapFor(id), id),
    [mapFor],
  );

  const restoreEvent = useCallback(
    (event: CalendarEvent) => {
      // Undo puts an event back where it came from: `mapFor` no longer knows (the id is
      // gone from both docs), so the caller's own copy of the event decides — a personal
      // event restored into the shared doc would be a privacy regression on an undo.
      const wasPersonal = hasPersonal && personalIds.has(event.id);
      const target = wasPersonal ? personalMap : map;
      restoreEventIn(target, event, { actorId: authorId });
    },
    [authorId, hasPersonal, map, personalIds, personalMap],
  );

  const excludeOccurrence = useCallback(
    (parentId: string, occurrenceStart: string) => {
      excludeOccurrenceIn(mapFor(parentId), parentId, occurrenceStart, { actorId: authorId });
    },
    [authorId, mapFor],
  );

  const detachOccurrence = useCallback(
    (parentId: string, occurrenceStart: string, patch: EventPatch): CalendarEvent | null => {
      // A detached occurrence stays in the same doc as its series — splitting one
      // occurrence out of a "Just me" event must not publish that occurrence. The helper
      // transacts through the map's own doc, so exdate + detached event land together.
      return detachOccurrenceIn(mapFor(parentId), parentId, occurrenceStart, patch, {
        actorId: authorId,
        newId,
      });
    },
    [authorId, mapFor],
  );

  return {
    events,
    byId,
    placementOf,
    setPlacement,
    createEvent,
    updateEvent,
    deleteEvent,
    restoreEvent,
    detachOccurrence,
    excludeOccurrence,
  };
}

export interface HouseholdData {
  members: MemberView[];
  byId: Map<string, MemberView>;
  birthdays: Birthday[];
}

/**
 * Members + birthdays from the `household` doc. The household UI is a later workstream,
 * so each client makes sure *its own* profile exists there (name/colour/emoji come from
 * the Postgres `users` row) — otherwise the calendar would have no colours to work with.
 * A client only ever writes its own entry, so this can't fight another member's edit.
 */
export function useHouseholdData(): HouseholdData {
  const { user } = useUser();
  const doc = useYDoc(HOUSEHOLD_DOC);
  const { map: memberMap, entries: memberEntries } = useYMap<HouseholdMember>(
    doc,
    HOUSEHOLD_MEMBERS_KEY,
  );
  const { entries: birthdayEntries } = useYMap<Birthday>(doc, HOUSEHOLD_BIRTHDAYS_KEY);

  useEffect(() => {
    ensureSelfRegistered(memberMap, user);
  }, [memberMap, user]);

  const members = useMemo(
    () =>
      toMemberViews(
        memberEntries
          .map(([, value]) => householdMemberSchema.safeParse(value))
          .flatMap((parsed) => (parsed.success ? [parsed.data] : [])),
      ),
    [memberEntries],
  );

  const birthdays = useMemo(
    () =>
      birthdayEntries
        .map(([, value]) => birthdaySchema.safeParse(value))
        .flatMap((parsed) => (parsed.success ? [parsed.data] : [])),
    [birthdayEntries],
  );

  return { members, byId: memberIndex(members), birthdays };
}

function ensureSelfRegistered(
  map: Y.Map<HouseholdMember>,
  user: { id: string; name: string; color: string; avatarEmoji?: string } | null,
): void {
  if (!user) return;
  const existing = map.get(user.id);
  const next: HouseholdMember = {
    id: user.id,
    name: user.name,
    color: user.color,
    ...(user.avatarEmoji ? { avatarEmoji: user.avatarEmoji } : {}),
  };
  if (
    existing &&
    existing.name === next.name &&
    existing.color === next.color &&
    existing.avatarEmoji === next.avatarEmoji
  ) {
    return;
  }
  map.set(user.id, next);
}

/**
 * Where my new events start out — the shared calendar or "Just me" (`prefs.ts` in the shared
 * calendar module). Read from, and written straight to, the `household` doc's
 * `calendarPrefs` map (no REST, ROADMAP §1.3), so it follows me across devices. A member
 * only ever writes their own entry.
 */
export function useNewEventPlacement(): {
  placement: NewEventPlacement;
  setPlacement: (next: NewEventPlacement) => void;
} {
  const { user } = useUser();
  const doc = useYDoc(HOUSEHOLD_DOC);
  const { map, entries } = useYMap<CalendarPref>(doc, HOUSEHOLD_CALENDAR_PREFS_KEY);
  const userId = user?.id ?? null;

  const placement = useMemo(() => {
    const mine = entries.find(([memberId]) => memberId === userId);
    return newEventPlacementFor(mine ? parseCalendarPref(mine[1]) : null);
  }, [entries, userId]);

  const setPlacement = useCallback(
    (next: NewEventPlacement) => {
      if (!userId) return;
      map.set(userId, { newEvents: next, updatedAt: new Date().toISOString() });
    },
    [map, userId],
  );

  return { placement, setPlacement };
}
